import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@heroui/button";
import { Input } from "@heroui/input";
import { DateValue } from "@heroui/react";
import { DatePicker } from "@heroui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip } from "@heroui/tooltip";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useDateFormatter } from "@react-aria/i18n";
import { getLocalTimeZone, parseDate } from "@internationalized/date";

import { Form } from "../../components/ui/form";

import {
  TUpdateAnimalSchema,
  updateAnimalSchema,
} from "@/models/Animal.Schema.ts";
import {
  birthConditions,
  breedingTypes,
  coatColor,
  frameSizes,
  nigerianBreeds,
  paternalTypes,
  ratios,
  statuses,
  vaccineArray,
} from "@/utils/items";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

import { AnimalImageInput } from "@/components/animal-image-input";
import { apiClient } from "@/api/api.ts";
import { useGetAllAnimals } from "@/features/Admin/Admin.api.ts";

const splitBreedComposition = (
  racialComposition?: string,
  breedCompositionRatio?: string,
) => {
  const composition = racialComposition?.trim() || "";
  const storedRatio = breedCompositionRatio?.trim() || "";

  if (storedRatio) {
    return {
      racialComposition: composition || undefined,
      breedCompositionRatio: storedRatio,
    };
  }

  const matchedRatio = ratios.find(({ value: ratio }) => {
    if (!composition.endsWith(ratio)) return false;

    const breed = composition.slice(0, -ratio.length).trim();

    return nigerianBreeds.some(({ breed: knownBreed }) => knownBreed === breed);
  });

  if (!matchedRatio) {
    return {
      racialComposition: composition || undefined,
      breedCompositionRatio: "",
    };
  }

  return {
    racialComposition: composition.slice(0, -matchedRatio.value.length).trim(),
    breedCompositionRatio: matchedRatio.value,
  };
};

const joinBreedComposition = (
  racialComposition?: string,
  breedCompositionRatio?: string,
) =>
  `${racialComposition?.trim() || ""}${breedCompositionRatio?.trim() || ""}`;

const AdminAnimalUpdate = ({ data, id }: any) => {
  const [activeTab, setActiveTab] = useState("basic");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [value, setValue] = useState<DateValue | null>(parseDate("2024-04-04"));
  const { data: allAnimals } = useGetAllAnimals();

  const singleAnimal =
    allAnimals?.find((animal: any) => animal._id === (id || data?._id)) ||
    (id ? undefined : data);

  const queryClient = useQueryClient();

  const formatter = useDateFormatter({ dateStyle: "full" });
  const breedComposition = splitBreedComposition(
    singleAnimal?.racialComposition,
    singleAnimal?.breedCompositionRatio,
  );

  const form = useForm<TUpdateAnimalSchema>({
    resolver: zodResolver(updateAnimalSchema),
    mode: "onChange",
    defaultValues: {
      gn: singleAnimal?.gn ? (singleAnimal.gn.startsWith("SG") ? singleAnimal.gn.slice(2) : singleAnimal.gn) : "",
      sex: singleAnimal?.sex || "",
      name: singleAnimal?.name || "",
      weight: singleAnimal?.weight ? Number(singleAnimal.weight) : undefined,
      vaccination: singleAnimal?.vaccination || "",
      damDGR: singleAnimal?.damDGR || "",
      damBreed: singleAnimal?.damBreed || "",
      damName: singleAnimal?.damName || "",
      sireDGR: singleAnimal?.sireDGR || "",
      sireName: singleAnimal?.sireName || "",
      sireBreed: singleAnimal?.sireBreed || "",
      age: singleAnimal?.age || "",
      paternalGrandDamDGR: singleAnimal?.paternalGrandDamDGR || "",
      paternalGrandDamName: singleAnimal?.paternalGrandDamName || "",
      paternalGrandDamBreed: singleAnimal?.paternalGrandDamBreed || "",
      paternalGrandSireDGR: singleAnimal?.paternalGrandSireDGR || "",
      paternalGrandSireName: singleAnimal?.paternalGrandSireName || "",
      paternalGrandSireBreed: singleAnimal?.paternalGrandSireBreed || "",
      maternalGrandDamDGR: singleAnimal?.maternalGrandDamDGR || "",
      maternalGrandDamName: singleAnimal?.maternalGrandDamName || "",
      maternalGrandDamBreed: singleAnimal?.maternalGrandDamBreed || "",
      maternalGrandSireDGR: singleAnimal?.maternalGrandSireDGR || "",
      maternalGrandSireName: singleAnimal?.maternalGrandSireName || "",
      maternalGrandSireBreed: singleAnimal?.maternalGrandSireBreed || "",
      animalStatus: singleAnimal?.animalStatus || "",
      birthWeight: singleAnimal?.birthWeight ? Number(singleAnimal.birthWeight) : undefined,
      coatColor: singleAnimal?.coatColor || "",
      size: singleAnimal?.size || "",
      birthCondition: singleAnimal?.birthCondition || undefined,
      breedingType: singleAnimal?.breedingType || "",
      paternalType: singleAnimal?.paternalType || "",
      racialComposition: breedComposition.racialComposition,
      breedCompositionRatio: breedComposition.breedCompositionRatio,
    },
  });

  useEffect(() => {
    if (singleAnimal) {
      const currentBreedComposition = splitBreedComposition(
        singleAnimal.racialComposition,
        singleAnimal.breedCompositionRatio,
      );

      form.reset({
        gn: singleAnimal?.gn ? (singleAnimal.gn.startsWith("SG") ? singleAnimal.gn.slice(2) : singleAnimal.gn) : "",
        sex: singleAnimal?.sex || "",
        name: singleAnimal?.name || "",
        weight: singleAnimal?.weight ? Number(singleAnimal.weight) : undefined,
        vaccination: singleAnimal?.vaccination || "",
        damDGR: singleAnimal?.damDGR || "",
        damBreed: singleAnimal?.damBreed || "",
        damName: singleAnimal?.damName || "",
        sireDGR: singleAnimal?.sireDGR || "",
        sireName: singleAnimal?.sireName || "",
        sireBreed: singleAnimal?.sireBreed || "",
        age: singleAnimal?.age || "",
        paternalGrandDamDGR: singleAnimal?.paternalGrandDamDGR || "",
        paternalGrandDamName: singleAnimal?.paternalGrandDamName || "",
        paternalGrandDamBreed: singleAnimal?.paternalGrandDamBreed || "",
        paternalGrandSireDGR: singleAnimal?.paternalGrandSireDGR || "",
        paternalGrandSireName: singleAnimal?.paternalGrandSireName || "",
        paternalGrandSireBreed: singleAnimal?.paternalGrandSireBreed || "",
        maternalGrandDamDGR: singleAnimal?.maternalGrandDamDGR || "",
        maternalGrandDamName: singleAnimal?.maternalGrandDamName || "",
        maternalGrandDamBreed: singleAnimal?.maternalGrandDamBreed || "",
        maternalGrandSireDGR: singleAnimal?.maternalGrandSireDGR || "",
        maternalGrandSireName: singleAnimal?.maternalGrandSireName || "",
        maternalGrandSireBreed: singleAnimal?.maternalGrandSireBreed || "",
        animalStatus: singleAnimal?.animalStatus || "",
        birthWeight: singleAnimal?.birthWeight ? Number(singleAnimal.birthWeight) : undefined,
        coatColor: singleAnimal?.coatColor || "",
        size: singleAnimal?.size || "",
        birthCondition: singleAnimal?.birthCondition || undefined,
        breedingType: singleAnimal?.breedingType || "",
        paternalType: singleAnimal?.paternalType || "",
        racialComposition: currentBreedComposition.racialComposition,
        breedCompositionRatio: currentBreedComposition.breedCompositionRatio,
      });

      if (singleAnimal.birthDate) {
        try {
          const parsed = parseDate(new Date(singleAnimal.birthDate).toISOString().split("T")[0]);
          setValue(parsed);
        } catch {
          // ignore
        }
      }
    }
  }, [singleAnimal, form]);

  const { mutateAsync } = useMutation({
    mutationKey: ["updateAnimal"],
    mutationFn: async (data) => {
      {
        try {
          const response = await apiClient.patch("/admin/updateAnimal", data);

          return response.data;
        } catch (error) {
          console.error(`Error updating animal: ${error}`);
          throw error;
        }
      }
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ["allAnimals"] });
      const previousAnimals = queryClient.getQueryData<any>(["allAnimals"]);

      queryClient.setQueryData<any>(["allAnimals"], (old: any) => {
        if (!old) return [];

        return old.map((animal: any) =>
          animal._id === id
            ? {
                ...animal,
                gn: data?.gn || "",
                sex: data?.sex || "",
                name: data?.name || "",
                weight: data?.weight || "",
                vaccination: data?.vaccination || "",
                damDGR: data?.damDGR || "",
                damBreed: data?.damBreed || "",
                damName: data?.damName || "",
                sireDGR: data?.sireDGR || "",
                sireName: data?.sireName || "",
                sireBreed: data?.sireBreed || "",
                age: data?.age || "",
                paternalGrandDamDGR: data?.paternalGrandDamDGR || "",
                paternalGrandDamName: data?.paternalGrandDamName || "",
                paternalGrandDamBreed: data?.paternalGrandDamBreed || "",
                paternalGrandSireDGR: data?.paternalGrandSireDGR || "",
                paternalGrandSireName: data?.paternalGrandSireName || "",
                paternalGrandSireBreed: data?.paternalGrandSireBreed || "",
                maternalGrandDamDGR: data?.maternalGrandDamDGR || "",
                maternalGrandDamName: data?.maternalGrandDamName || "",
                maternalGrandDamBreed: data?.maternalGrandDamBreed || "",
                maternalGrandSireDGR: data?.maternalGrandSireDGR || "",
                maternalGrandSireName: data?.maternalGrandSireName || "",
                maternalGrandSireBreed: data?.maternalGrandSireBreed || "",
                animalStatus: data?.animalStatus || "",
                birthWeight: data?.birthWeight || "",
                coatColor: data?.coatColor || "",
                size: data?.size || "",
                birthCondition: data?.birthCondition || "",
                breedingType: data?.breedingType || "",
                paternalType: data?.paternalType || "",
                racialComposition: data?.racialComposition || "",
              }
            : animal,
        );
      });

      return { previousAnimals };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allAnimals"] });
      toast("The animal information has been updated successfully.");
      // onClose ()
    },
    onError: (error: Error, context: any) => {
      if (error instanceof AxiosError) {
        toast(error.response?.data.message);
      }
      toast("Failed to update animal details. Reverting...");
      queryClient.setQueryData(["allAnimals"], context?.previousAnimals);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["allAnimals"] });
      queryClient.invalidateQueries({ queryKey: ["animals"] });
      queryClient.invalidateQueries({ queryKey: ["SilaAnimal", id || singleAnimal?._id] });
      queryClient.invalidateQueries({ queryKey: ["farmerOwnedAnimals"] });
    },
  });


  const onSubmit: SubmitHandler<TUpdateAnimalSchema> = async () => {
    try {
      if (!value) throw new Error("Birth date value is null");

      const animalData = {
        id: id || singleAnimal?._id,
        gn: "SG" + form.getValues().gn,
        birthDate: value
          ? formatter.format(value.toDate(getLocalTimeZone()))
          : "",
        sex: form.getValues().sex,
        name: form.getValues().name,
        age: form.getValues().age,
        size: form.getValues().size,
        weight: Number(form.getValues().weight),
        birthWeight: Number(form.getValues().birthWeight),
        coatColor: form.getValues().coatColor,
        breedingType: form.getValues().breedingType,
        damName: form.getValues().damName,
        damDGR: form.getValues().damDGR,
        damBreed: form.getValues().damBreed,
        sireDGR: form.getValues().sireDGR,
        sireName: form.getValues().sireName,
        sireBreed: form.getValues().sireBreed,
        paternalGrandDamDGR: form.getValues().paternalGrandDamDGR,
        paternalGrandDamName: form.getValues().paternalGrandDamName,
        paternalGrandDamBreed: form.getValues().paternalGrandDamBreed,
        paternalGrandSireDGR: form.getValues().paternalGrandSireDGR,
        paternalGrandSireName: form.getValues().paternalGrandSireName,
        paternalGrandSireBreed: form.getValues().paternalGrandSireBreed,
        maternalGrandDamDGR: form.getValues().maternalGrandDamDGR,
        maternalGrandDamName: form.getValues().maternalGrandDamName,
        maternalGrandDamBreed: form.getValues().maternalGrandDamBreed,
        maternalGrandSireDGR: form.getValues().maternalGrandSireDGR,
        maternalGrandSireName: form.getValues().maternalGrandSireName,
        maternalGrandSireBreed: form.getValues().maternalGrandSireBreed,
        birthCondition: form.getValues().birthCondition,
        paternalType: form.getValues().paternalType,
        racialComposition: joinBreedComposition(
          form.getValues().racialComposition,
          form.getValues().breedCompositionRatio,
        ),
        vaccination: form.getValues().vaccination,
        animalStatus: form.getValues().animalStatus,
        farmID: data?.farm,
      };

      const imageUpdate = imageFile
        ? { image: await (await import("@/utils/uploadAnimalImage")).uploadAnimalImage(imageFile) }
        : {};
      const update = mutateAsync({ ...animalData, ...imageUpdate });
      toast.promise(update, {
        loading: "Updating animal...",
        success: "Animal details updated successfully!",
        error: "Failed to update animal details.",
      });
      await update;
      setImageFile(null);
    } catch (error) {
      console.error("caught error", error);
      toast.error(error instanceof Error ? error.message : "Failed to update animal details.");
    }
  };

  const tabs = [
    { id: "basic", label: "Basic Info" },
    { id: "family", label: "Family Info" },
    { id: "additional", label: "Additional Info" },
  ];

  return (
    <div className="container mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="col-span-1 dark:bg-gray-900">
          <CardContent className={"py-2"}>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="bg-primary-100 text-gray-800 dark:bg-primary-300 grid w-full grid-cols-3">
                    {tabs.map((tab) => (
                      <TabsTrigger
                        key={tab.id}
                        className="data-[state=active]:dark:bg-amber-600 data-[state=active]:bg-amber-600 data-[state=active]:text-primary-foreground"
                        value={tab.id}
                      >
                        {tab.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTab}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      initial={{ opacity: 0, y: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <TabsContent className="mt-4" value="basic">
                        <ScrollArea className="h-[60vh] pr-4">
                          <BasicInfoFields
                            form={form}
                            receivedData={singleAnimal}
                            setValue={(e: any) => setValue(e)}
                            value={value}
                          />
                        </ScrollArea>
                      </TabsContent>
                      <TabsContent className="mt-4" value="family">
                        <ScrollArea className="h-[60vh] pr-4">
                          <FamilyInfoFields
                            form={form}
                            receivedData={singleAnimal}
                          />
                        </ScrollArea>
                      </TabsContent>
                      <TabsContent className="mt-4 " value="additional">
                        <ScrollArea className="h-[60vh] pr-4">
                          <AdditionalInfoFields
                            form={form}
                            receivedData={singleAnimal}
                            imageFile={imageFile}
                            onImageChange={setImageFile}
                          />
                        </ScrollArea>
                      </TabsContent>
                    </motion.div>
                  </AnimatePresence>
                </Tabs>
                <Button
                  className="w-full mt-6"
                  color={"primary"}
                  radius={"sm"}
                  type="submit"
                  isDisabled={form.formState.isSubmitting}
                  isLoading={form.formState.isSubmitting}
                >
                  Submit Changes
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Received Animal Data</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[70vh] pr-4">
              {/*<AnimatePresence >*/}
              {/*	{ Object.entries ( data ).map ( ( [ key, value ], index ) => (*/}
              {/*			<motion.div*/}
              {/*					key={ `${ key }-${ index }` }  // Ensure the key is unique by combining key and index*/}
              {/*					initial={ { opacity: 0, y: 20 } }*/}
              {/*					animate={ { opacity: 1, y: 0 } }*/}
              {/*					transition={ { duration: 0.3, delay: index * 0.05 } }*/}
              {/*					className="mb-4"*/}
              {/*			>*/}
              {/*				<Label*/}
              {/*						className="text-sm font-medium text-muted-foreground" >*/}
              {/*					{ key }*/}
              {/*				</Label >*/}
              {/*				<div className="mt-1 p-2 bg-muted rounded-md" >*/}
              {/*      <span className="text-base font-semibold" >*/}
              {/*        { value as string }*/}
              {/*      </span >*/}
              {/*				</div >*/}
              {/*			</motion.div >*/}
              {/*	) ) }*/}
              {/*</AnimatePresence >*/}
              <AnimatePresence>
                {Object.entries(data || {}).map(([key, value], index) => (
                  <motion.div
                    key={`${key}-${index}`}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4"
                    initial={{ opacity: 0, y: 20 }}
                    transition={{
                      duration: 0.3,
                      delay: index * 0.05,
                    }}
                  >
                    <Label className="text-sm font-medium text-muted-foreground">
                      {key}
                    </Label>
                    <div className="mt-1 p-2 bg-muted rounded-md">
                      <span className="text-base font-semibold">
                        {value as string} {/* This is where the error occurs */}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const BasicInfoFields = ({
  form,
  receivedData,
  value,
  setValue,
}: {
  form: any;
  receivedData: any;
  setValue: (value: DateValue) => void;
  value: any;
}) => {
  const { control } = form;

  return (
    <div className="space-y-4">
      <Controller
        control={control}
        name="gn"
        render={({ field }) => (
          <InputField
            field={field}
            label="Birth Registry ID"
            name="gn"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="name"
        render={({ field }) => (
          <InputField
            field={field}
            label="Name"
            name="name"
            receivedData={receivedData}
          />
        )}
      />
      <div className="space-y-2 ">
        <Tooltip
          color="secondary"
          content="The Birth Date of The Animal"
          delay={500}
          offset={10}
        >
          <DatePicker
            showMonthAndYearPickers
            calendarProps={{
              focusedValue: value,
              onFocusChange: setValue,
              nextButtonProps: {
                variant: "bordered",
              },
              prevButtonProps: {
                variant: "bordered",
              },
            }}
            className={"w-[300px]"}
            description={"date format mm/dd/yyyy"}
            label="Birth date"
            labelPlacement={"outside"}
            size="sm"
            value={value}
            onChange={(val) => setValue(val as DateValue)}
          />
        </Tooltip>
      </div>
      <Controller
        control={control}
        name="sex"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Sex"
            name="sex"
            options={[
              { value: "male", label: "Male" },
              { value: "female", label: "Female" },
            ]}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="age"
        render={({ field }) => (
          <InputField
            field={field}
            label="Age"
            name="age"
            receivedData={receivedData}
            type="number"
          />
        )}
      />
      <Controller
        control={control}
        name="weight"
        render={({ field }) => (
          <InputField
            field={field}
            label="Weight"
            name="weight"
            receivedData={receivedData}
            type="number"
          />
        )}
      />
      <Controller
        control={control}
        name="birthWeight"
        render={({ field }) => (
          <InputField
            field={field}
            label="Birth Weight"
            name="birthWeight"
            receivedData={receivedData}
            type="string"
          />
        )}
      />
      <Controller
        control={control}
        name="animalStatus"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Status"
            name="animalStatus"
            options={statuses.map((s) => ({
              value: s.uid,
              label: s.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="vaccination"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Vaccines"
            name="vaccination"
            options={vaccineArray.map((v) => ({
              value: v.value,
              label: v.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="breedingType"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Breeding Type"
            name="breedingType"
            options={breedingTypes.map((b) => ({
              value: b.name,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="size"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Size"
            name="size"
            options={frameSizes.map((s) => ({
              value: s.value,
              label: s.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="coatColor"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Coat Color"
            name="coatColor"
            options={coatColor.map((c) => ({
              value: c.coatColor,
              label: c.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="racialComposition"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Racial Composition"
            name="racialComposition"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="breedCompositionRatio"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Breed Composition Ratio"
            name="breedCompositionRatio"
            options={ratios.map((r) => ({
              value: r.value,
              label: r.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
    </div>
  );
};

const FamilyInfoFields = ({ form, receivedData }: any) => {
  const { control } = form;

  return (
    <div className="space-y-4">
      <Controller
        control={control}
        name="damName"
        render={({ field }) => (
          <InputField
            field={field}
            label="Dam Name"
            name="damName"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="damDGR"
        render={({ field }) => (
          <InputField
            field={field}
            label="Dam DGR"
            name="damDGR"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="damBreed"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Dam Breed"
            name="damBreed"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="sireName"
        render={({ field }) => (
          <InputField
            field={field}
            label="Sire Name"
            name="sireName"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="sireDGR"
        render={({ field }) => (
          <InputField
            field={field}
            label="Sire DGR"
            name="sireDGR"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="sireBreed"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Sire Breed"
            name="sireBreed"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="paternalGrandDamName"
        render={({ field }) => (
          <InputField
            field={field}
            label="Paternal GrandDam Name"
            name="paternalGrandDamName"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="paternalGrandDamDGR"
        render={({ field }) => (
          <InputField
            field={field}
            label="Paternal GrandDam DGR"
            name="paternalGrandDamDGR"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="paternalGrandDamBreed"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Paternal GrandDam Breed"
            name="paternalGrandDamBreed"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="paternalGrandSireName"
        render={({ field }) => (
          <InputField
            field={field}
            label="Paternal GrandSire Name"
            name="paternalGrandSireName"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="paternalGrandSireDGR"
        render={({ field }) => (
          <InputField
            field={field}
            label="Paternal GrandSire DGR"
            name="paternalGrandSireDGR"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="paternalGrandSireBreed"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Paternal GrandSire Breed"
            name="paternalGrandSireBreed"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="maternalGrandDamName"
        render={({ field }) => (
          <InputField
            field={field}
            label="Maternal GrandDam Name"
            name="maternalGrandDamName"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="maternalGrandDamDGR"
        render={({ field }) => (
          <InputField
            field={field}
            label="Maternal GrandDam DGR"
            name="maternalGrandDamDGR"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="maternalGrandDamBreed"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Maternal GrandDam Breed"
            name="maternalGrandDamBreed"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="maternalGrandSireName"
        render={({ field }) => (
          <InputField
            field={field}
            label="Maternal GrandSire Name"
            name="maternalGrandSireName"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="maternalGrandSireDGR"
        render={({ field }) => (
          <InputField
            field={field}
            label="Maternal GrandSire DGR"
            name="maternalGrandSireDGR"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="maternalGrandSireBreed"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Maternal GrandSire Breed"
            name="maternalGrandSireBreed"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
    </div>
  );
};

const AdditionalInfoFields = ({ form, receivedData, imageFile, onImageChange }: any) => {
  const { control } = form;

  return (
    <div className="w-full space-y-4 flex flex-col">
      <div className={"w-full flex flex-col gap-4"}>
        <Controller
          control={control}
          name="paternalType"
          render={({ field }) => (
            <SelectField
              field={field}
              label="Paternal Type"
              name="paternalType"
              options={paternalTypes.map((t) => ({
                value: t.value,
                label: t.label,
              }))}
              receivedData={receivedData}
            />
          )}
        />
        <Controller
          control={control}
          name="birthCondition"
          render={({ field }) => (
            <SelectField
              field={field}
              label="Birth Condition"
              name="birthCondition"
              options={birthConditions.map((c) => ({
                value: c.uid,
                label: c.label,
              }))}
              receivedData={receivedData}
            />
          )}
        />
      </div>
      <AnimalImageInput file={imageFile} currentImage={receivedData?.image} onChange={onImageChange} disabled={form.formState.isSubmitting} />
    </div>
  );
};

// const InputField = ( ( {
// 	                                  name, label, type, field,
// receivedData } : any ) => { return ( <div
// className="space-y-2" > <Label htmlFor={ name } >{ label
// }</Label > <Input id={ name } name={ name } type={ type }
// value={ field.value || "" } onChange={ field.onChange }
// className="w-full" size={ "sm" } variant={ "faded" } /> {
// receivedData[ name ] ? ( <p className="text-sm
// text-muted-foreground flex items-center" > <span
// className="mr-1" >Current:</span > <span
// className="font-medium" >{ receivedData[ name ] }</span > {
// field.value && field.value !== receivedData[ name ] && (
// <motion.span initial={ { opacity: 0, x: - 10 } } animate={ {
// opacity: 1, x: 0 } } className="ml-2 text-primary" >
// <CheckCircle2 className="w-4 h-4 inline-block mr-1" />
// Changed </motion.span > )} </p > ) } </div > ) } )

const InputField = ({
  name,
  label,
  type,
  field,
  receivedData = {}, // Default to an empty
  // object
}: any) => {
  return (
    <div className="space-y-2">
      <Label htmlFor={name}>{label}</Label>
      <Input
        className="w-full "
        classNames={{
          inputWrapper: "dark:bg-slate-950",
        }}
        color={"primary"}
        id={name}
        name={name}
        size={"sm"}
        type={type}
        value={field.value || ""}
        variant={"faded"}
        onChange={field.onChange}
      />
      {receivedData[name] !== undefined && ( // Check if receivedData[name] is defined
        <p className="text-sm text-muted-foreground flex items-center">
          <span className="mr-1">Current:</span>
          <span className="font-medium">{receivedData[name]}</span>
          {field.value && field.value !== receivedData[name] && (
            <motion.span
              animate={{ opacity: 1, x: 0 }}
              className="ml-2 text-primary dark:text-rose-400"
              initial={{ opacity: 0, x: -10 }}
            >
              <CheckCircle2 className="w-4 h-4 inline-block mr-1" />
              Changed
            </motion.span>
          )}
        </p>
      )}
    </div>
  );
};

const SelectField = ({
  name,
  label,
  options,
  field,
  receivedData = {},
}: any) => {
  const [triggerElement, setTriggerElement] = useState<HTMLButtonElement | null>(null);

  return (
    <div className="space-y-2">
      <Label htmlFor={name}>{label}</Label>
      <Select
        defaultValue={field.value || undefined}
        value={field.value || undefined}
        onValueChange={field.onChange}
      >
        <SelectTrigger ref={setTriggerElement} id={name} className="w-full">
          <SelectValue placeholder={`Select ${label}`} />
        </SelectTrigger>
        <SelectContent
          position="popper"
          // Keep options inside HeroUI's modal focus scope.
          container={triggerElement?.closest<HTMLElement>('[role="dialog"]')}
          className="max-h-[300px] overflow-y-auto z-[9999] shadow-lg bg-white text-slate-950 dark:bg-slate-950 dark:text-slate-50"
        >
          {options.map((option: any) => (
            <SelectItem
              key={String(option.value)}
              value={String(option.value)}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {receivedData[name] !== undefined && (
        <p className="text-sm text-muted-foreground flex items-center">
          <span className="mr-1">Current:</span>
          <span className="font-medium">{receivedData[name]}</span>
          {field.value &&
            String(field.value) !== String(receivedData[name]) && (
              <motion.span
                animate={{ opacity: 1, x: 0 }}
                className="ml-2 text-primary dark:text-rose-400"
                initial={{ opacity: 0, x: -10 }}
              >
                <CheckCircle2 className="w-4 h-4 inline-block mr-1" />
                Changed
              </motion.span>
            )}
        </p>
      )}
    </div>
  );
};

export default AdminAnimalUpdate;
